import { Upload } from '@aws-sdk/lib-storage';
import {
  Box,
  IconButton,
  List,
  ListItem,
  Modal,
  ModalOverlay,
  Portal,
  Progress,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tooltip,
  useDisclosure,
  useTabPanel,
  useToast,
} from '@chakra-ui/react';
import { faEye, faEyeSlash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useFlag } from '@unleash/proxy-client-react';
import { Dictionary, groupBy, intersection, sortBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useClient, useMutation, useQuery } from 'urql';
import awsExports from '../../aws-exports';
import {
  DeleteModelVersionDocument,
  EventResource,
  EventType,
  GetModelsForProjectDocument,
  GetModelsForProjectQuery,
  GetNextModelVersionDocument,
  TranslationType,
} from '../../gql/graphql';
import { useLocalStorage } from '../../hooks/local-storage';
import { useS3Client } from '../../hooks/s3';
import { Sheet, useSheets } from '../../hooks/sheets';
import { useUserTenant } from '../../services/auth-info';
import { EventName, useMixpanel } from '../../services/mixpanel';
import { tierLimits } from '../../utils/tier-utils';
import { useBreadcrumb } from '../common/BreadcrumbProvider';
import { useViewer } from '../common/ForgeViewer';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../common/Panel';
import { useViewerMode, ViewerMode } from '../common/viewer-mode';
import DragAndDrop from '../dragAndDrop/DragAndDrop';
import PlaceholderText from '../text/PlaceholderText';
import BimModelItem from './BIMModelItem';
import ConfirmModelUpdateModal from './ConfirmChangeModelModal';
import { Buy } from './project-modal/Buy';
import SheetItem from './SheetItem';
import { getHistoricalActiveModels } from './project-history/hooks/useDiffing';
import { useProjectHistory } from './project-history/hooks/useProjectHistory';
import { useEnabledSheetViewerModeValue } from 'src/components/viewers/common/hooks/useEnabledSheetViewerModeValue';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
import { useEventHistory } from 'src/hooks/event-history';

type ModelsPanelProps = {
  projectId: string;
  translationType: TranslationType | null | undefined;
  readOnly: boolean;
};

export default function ModelsPanel({
  projectId,
  translationType,
  readOnly,
}: ModelsPanelProps) {
  const panelActionsContainerRef = useRef<HTMLDivElement>(null);
  const panelContentRef = useRef<HTMLDivElement>(null);
  const { viewerMode, setViewerMode } = useViewerMode();
  const { sheetsViewerMode } = useEnabledSheetViewerModeValue();

  const [modelsPanelHeight, setModelsPanelHeight] = useLocalStorage<
    number | undefined
  >('models-panel-height', undefined);

  const { isProtan } = useUserTenant();
  const tabIndex = viewerMode === ViewerMode.Models || isProtan ? 0 : 1;

  const handleTabChange = (index: number) => {
    if (isProtan) {
      setViewerMode(ViewerMode.Sheets);
    } else {
      setViewerMode(index === 0 ? ViewerMode.Models : sheetsViewerMode);
    }
  };

  const { t } = useTranslation('project');

  return (
    <Panel
      resizeable
      panelContentRef={panelContentRef}
      setPanelHeight={setModelsPanelHeight}
    >
      <Tabs variant="panel-tabs" index={tabIndex} onChange={handleTabChange}>
        <PanelHeader hasNoMarginBottom>
          <PanelTitle>
            <TabList>
              {!isProtan && <Tab>{t('project:files.models-tab.title')}</Tab>}
              <Tab>{t('project:files.sheets-tab.title')}</Tab>
            </TabList>
          </PanelTitle>
          <Box ref={panelActionsContainerRef} />
        </PanelHeader>
        <PanelContent
          padding={0}
          marginX={-4}
          overflowY={'auto'}
          ref={panelContentRef}
          height={modelsPanelHeight}
        >
          <TabPanels>
            {!isProtan && (
              <TabPanel>
                <BIMModelsPanel
                  projectId={projectId}
                  translationType={translationType}
                  readOnly={readOnly}
                  panelActionsContainerRef={panelActionsContainerRef}
                />
              </TabPanel>
            )}
            <TabPanel>
              <SheetPane
                projectId={projectId}
                readOnly={readOnly}
                panelActionsContainerRef={panelActionsContainerRef}
              />
            </TabPanel>
          </TabPanels>
        </PanelContent>
      </Tabs>
    </Panel>
  );
}

type BIMModelsPaneProps = {
  projectId: string;
  translationType: TranslationType | null | undefined;
  panelActionsContainerRef: React.RefObject<HTMLDivElement>;
  readOnly: boolean;
};

function BIMModelsPanel({
  projectId,
  translationType,
  panelActionsContainerRef,
  readOnly,
}: BIMModelsPaneProps): React.ReactElement {
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const tabPanel = useTabPanel({});
  const shouldRenderActionButtons = !tabPanel.hidden;

  const [modelsUploaded, setModelsUploaded] = useState(false);
  const toast = useToast();
  const { tenant, tenantTier, username } = useUserTenant();
  const enablePayment = useFlag('enable-payments');
  const s3Client = useS3Client(projectId);
  const { trackEvent } = useMixpanel();
  const { logEvent } = useEventHistory(projectId);

  const { projectName } = useBreadcrumb();

  const {
    isOpen: confirmUpdateIsOpen,
    onOpen: openConfirmUpdate,
    onClose: closeConfirmUpdate,
  } = useDisclosure();

  const {
    visibilityManager: { showAllModels, hideAllModels },
  } = useViewer();

  const [modelVisibilityState, setModelVisibilityState] = useState<
    'all' | 'none'
  >('all');

  const [filesToConfirm, setFilesToConfirm] = useState<FileList | null>(null);
  const [showPaywallModal, setShowPaywallModal] = useState(false);

  const { isHistoryDrawerOpen, diff, referenceState } = useProjectHistory();

  const [{ data: bimModelsData }] = useQuery({
    query: GetModelsForProjectDocument,
    variables: { projectId },
  });

  const urqlClient = useClient();

  const modelVersions: Dictionary<GetModelsForProjectQuery['models']> =
    useMemo(() => {
      // Actually this implies that if diff and ref state are missing, we show the current models which is incorrect
      // I guess we are writing code this way to satisfy the type checker while infact this condition should be an invariant in the code
      let modelsForProject = bimModelsData?.models ?? [];
      if (isHistoryDrawerOpen && diff && referenceState) {
        modelsForProject = getHistoricalActiveModels(referenceState, diff);
      }
      return groupBy(modelsForProject, (model) => model.name);
    }, [bimModelsData?.models, referenceState, diff, isHistoryDrawerOpen]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [, deleteModel] = useMutation(DeleteModelVersionDocument);

  const { t } = useTranslation(['project', 'paywall']);

  const uploadModel = async (file: File, versionName: string) => {
    setModelsUploaded(true);
    setUploadProgress(0);
    setUploading(true);
    try {
      invariant(tenant, 'Tenant is required to upload models');
      const { data, error } = await urqlClient.query(
        GetNextModelVersionDocument,
        { projectId, versionName },
        { requestPolicy: 'network-only' }
      );
      if (error) {
        throw error;
      }

      const upload = new Upload({
        client: s3Client,
        params: {
          Bucket: awsExports.models_bucket_name,
          Key: `${tenant.group}/project/${projectId}/bimModels/${data?.getNextModelVersion}/${file.name}`,
          Body: file,
          Metadata: {
            modelname: encodeURIComponent(versionName),
            projectId: projectId,
            userEmail: username,
            translationType: translationType ? translationType : 'modern',
          },
        },
      });

      upload.on('httpUploadProgress', (progress) => {
        if (progress.loaded && progress.total) {
          setUploadProgress(100 * (progress.loaded / progress.total));
        }
      });

      await upload.done();

      // Check if this is a new model or an update to existing model
      const isNewModel = !modelVersions[versionName];

      logEvent({
        eventName: isNewModel ? 'Model Added' : 'Model Updated',
        eventType: EventType.Created,
        resourceName: versionName,
        resourceType: EventResource.Model,
        metadata: {
          affectedResources: [versionName],
          details: isNewModel
            ? `New model "${versionName}" uploaded`
            : `Model "${versionName}" updated with new version`,
        },
      });

      toast({
        status: 'success',
        duration: 3000,
        title: t('project:toast.success.model-upload'),
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('project:toast.error.model-upload'),
      });
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteModel = async (versionName: string) => {
    const { error } = await deleteModel({ projectId, versionName });

    if (error) {
      toast({
        title: t('project:toast.error.model-delete'),
        status: 'error',
      });
    } else {
      logEvent({
        eventName: 'Model Deleted',
        eventType: EventType.Deleted,
        resourceName: versionName,
        resourceType: EventResource.Model,
        metadata: {
          affectedResources: [versionName],
          details: `Model "${versionName}" deleted`,
        },
      });

      toast({
        title: t('project:toast.success.model-delete'),
        status: 'success',
      });
    }
  };

  const handleUploadBIMModelFile = (files: FileList) => {
    const maxModelsReached =
      tenantTier === 'FREE' &&
      enablePayment &&
      modelVersions &&
      Object.keys(modelVersions).length >= tierLimits.free.MAX_MODELS;

    if (maxModelsReached) {
      setShowPaywallModal(true);
      return;
    }

    const ABSOLUTE_MAX_MODELS = 99;

    if (
      modelVersions &&
      Object.keys(modelVersions).length + files.length > ABSOLUTE_MAX_MODELS
    ) {
      toast({
        title: t('paywall:model-limit-reached.title'),
        description: t('paywall:model-limit-reached.subtitle'),
        status: 'error',
      });
      return;
    }

    const fileNames = [...files].map((file) => file.name);
    const existingVersions = Object.keys(modelVersions);
    if (intersection(fileNames, existingVersions).length > 0) {
      setFilesToConfirm(files);
      openConfirmUpdate();
    } else {
      return Promise.all(
        [...files].map((file) => uploadModel(file, file.name))
      );
    }

    trackEvent(EventName.FileUploaded, {
      'File Type': 'ifc',
      'Project Name': projectName,
      'Project ID': projectId,
      'File Names': fileNames,
    });
  };

  const handleUploadExistingVersion = async (
    e: React.ChangeEvent<HTMLInputElement>,
    versionName: string
  ) => {
    const file = e.target?.files?.[0];
    invariant(file, 'No file was uploaded');
    await uploadModel(file, versionName);
  };

  const handleUploadBIMModelNewVersion = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target?.files;
    invariant(files, 'No file was uploaded');
    handleUploadBIMModelFile(files);
  };

  const handleBIMUpload = () => {
    inputRef.current?.click();
  };

  return (
    <>
      {shouldRenderActionButtons ? (
        <Portal containerRef={panelActionsContainerRef}>
          {!readOnly ? (
            <PanelActionButtons>
              {modelVisibilityState === 'all' ? (
                <Tooltip label={t('project:files.hide-all-models')}>
                  <IconButton
                    icon={<SparkelIcon icon={faEyeSlash} color={'inherit'} />}
                    colorScheme="gray"
                    aria-label="Hide all models"
                    size="xs"
                    isRound
                    onClick={() => {
                      setModelVisibilityState('none');
                      hideAllModels();
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip label={t('project:files.show-all-models')}>
                  <IconButton
                    icon={<SparkelIcon icon={faEye} color={'inherit'} />}
                    colorScheme="gray"
                    aria-label="Show all models"
                    size="xs"
                    isRound
                    onClick={() => {
                      setModelVisibilityState('all');
                      showAllModels();
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip label={t('project:files.add-bim-model')}>
                <IconButton
                  icon={
                    <SparkelIcon
                      icon={faPlus}
                      color={['gray.50', 'gray.700']}
                    />
                  }
                  onDrop={(e) => {
                    e.preventDefault();
                    handleUploadBIMModelFile(e.dataTransfer.files);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                  }}
                  colorScheme="brand"
                  aria-label="Add BIM model"
                  size="xs"
                  borderRadius="xl"
                  onClick={handleBIMUpload}
                  isDisabled={isUploading}
                />
              </Tooltip>
            </PanelActionButtons>
          ) : null}
        </Portal>
      ) : null}
      {!readOnly && !isHistoryDrawerOpen ? (
        <DragAndDrop
          uploadFunction={handleUploadBIMModelFile}
          allowedExtensions={['ifc', 'rvt']}
        >
          {isUploading ? (
            <Progress value={uploadProgress} colorScheme="brand" />
          ) : null}
          <input
            ref={inputRef}
            type="file"
            multiple={true}
            accept=".ifc, .rvt"
            onChange={handleUploadBIMModelNewVersion}
            style={{ display: 'none' }}
          />
          {!modelsUploaded && Object.keys(modelVersions).length === 0 ? (
            <PlaceholderText>
              <Trans
                defaults={t('project:files.upload-model-placeholder')}
                components={{
                  plusIcon: (
                    <SparkelIcon
                      size="sm"
                      icon={faPlus}
                      color={['gray.500', 'gray.400']}
                    />
                  ),
                }}
              />
            </PlaceholderText>
          ) : (
            <ModelList
              modelVersions={modelVersions}
              isUploading={isUploading}
              handleUploadExistingVersion={handleUploadExistingVersion}
              handleDeleteModel={handleDeleteModel}
              readOnly={readOnly}
            />
          )}
        </DragAndDrop>
      ) : (
        <Box padding={2} marginX={2}>
          <ModelList
            modelVersions={modelVersions}
            isUploading={isUploading}
            handleUploadExistingVersion={handleUploadExistingVersion}
            handleDeleteModel={handleDeleteModel}
            readOnly={readOnly}
          />
        </Box>
      )}
      <ConfirmModelUpdateModal
        isOpen={confirmUpdateIsOpen && !!filesToConfirm}
        onConfirm={async () => {
          invariant(filesToConfirm, 'No file to confirm');
          closeConfirmUpdate();
          await Promise.all(
            [...filesToConfirm].map((file) => uploadModel(file, file.name))
          );
          setFilesToConfirm(null);
        }}
        onClose={closeConfirmUpdate}
      />
      <Modal
        size={'full'}
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
      >
        <ModalOverlay />
        <Buy
          title={t('paywall:models.title')}
          subtitle={t('paywall:models.subtitle')}
          onClose={() => setShowPaywallModal(false)}
        />
      </Modal>
    </>
  );
}

type SheetPaneProps = {
  projectId: string;
  panelActionsContainerRef: React.RefObject<HTMLDivElement>;
  readOnly: boolean;
};

function SheetPane({
  projectId,
  panelActionsContainerRef,
  readOnly,
}: SheetPaneProps): React.ReactElement {
  const [isUploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const tabPanel = useTabPanel({});
  const shouldRenderActionButtons = !tabPanel.hidden;

  const { projectName } = useBreadcrumb();
  const { trackEvent } = useMixpanel();
  const { logEvent } = useEventHistory(projectId);

  const { t } = useTranslation(['project', 'paywall']);

  const toast = useToast();

  const { sheets, createSheet } = useSheets(projectId);
  const { activeSheetId, changeActiveSheet } = useEnabledSheetViewer();
  const {
    isOpen: confirmUpdateIsOpen,
    onOpen: openConfirmUpdate,
    onClose: closeConfirmUpdate,
  } = useDisclosure();
  const [filesToConfirm, setFilesToConfirm] = useState<FileList | null>(null);

  useEffect(() => {
    if (activeSheetId === null && sheets !== null && sheets.length > 0) {
      changeActiveSheet(sheets[0].id);
    }
  }, [activeSheetId, changeActiveSheet, sheets]);

  const sheetInputRef = useRef<HTMLInputElement>(null);

  const uploadSheet = async (
    file: File,
    version: number,
    isUpdate: boolean
  ) => {
    invariant(
      awsExports.attachments_bucket_name,
      'Bucket name is required to upload sheets'
    );

    try {
      // TODO: Allow user to set a name for the sheet
      const filename = file.name;
      const created = await createSheet(file, filename, version);

      if (created?.id) {
        changeActiveSheet(created.id);
      }

      logEvent({
        eventName: isUpdate ? 'Sheet Updated' : 'Sheet Added',
        eventType: isUpdate ? EventType.Modified : EventType.Created,
        resourceName: filename,
        resourceType: EventResource.Sheet,
        metadata: {
          affectedResources: [filename],
          details: isUpdate
            ? `Sheet "${filename}" updated to version ${version}`
            : `New sheet "${filename}" uploaded`,
        },
      });

      toast({
        status: 'success',
        duration: 3000,
        title: t('project:toast.success.sheet-upload'),
      });
    } catch (err) {
      toast({
        status: 'error',
        duration: 3000,
        title: t('project:toast.error.sheet-upload'),
      });
    }
  };

  const handleUploadSheetFile = (files: FileList) => {
    const fileNames = [...files].map((file) => file.name);
    const existingByName = new Map((sheets ?? []).map((s) => [s.name, s]));

    const hasConflicts = fileNames.some((n) => existingByName.has(n));
    if (hasConflicts) {
      setFilesToConfirm(files);
      openConfirmUpdate();
    } else {
      void Promise.all([...files].map((file) => uploadSheet(file, 1, false)));
    }

    trackEvent(EventName.FileUploaded, {
      'File Type': 'pdf',
      'Project Name': projectName,
      'Project ID': projectId,
      'File Names': fileNames,
    });
  };

  const handleUploadSheetNewVersion = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target?.files;
    invariant(files, 'No file was uploaded');
    handleUploadSheetFile(files);
  };

  const handleSheetUpload = () => {
    sheetInputRef.current?.click();
  };

  return (
    <>
      {shouldRenderActionButtons ? (
        <Portal containerRef={panelActionsContainerRef}>
          {!readOnly ? (
            <PanelActionButtons>
              <Tooltip label={t('project:files.add-sheet')}>
                <IconButton
                  icon={
                    <SparkelIcon
                      icon={faPlus}
                      color={['gray.50', 'gray.700']}
                    />
                  }
                  colorScheme="brand"
                  aria-label="Add sheet"
                  size="xs"
                  borderRadius="xl"
                  onClick={handleSheetUpload}
                  isDisabled={isUploading}
                />
              </Tooltip>
            </PanelActionButtons>
          ) : null}
        </Portal>
      ) : null}
      {!readOnly ? (
        <DragAndDrop
          uploadFunction={handleUploadSheetFile}
          allowedMimeTypes={['application/pdf']}
        >
          {isUploading ? (
            <Progress value={uploadProgress} colorScheme="brand" />
          ) : null}
          <input
            ref={sheetInputRef}
            type="file"
            multiple={true}
            accept="application/pdf"
            onChange={handleUploadSheetNewVersion}
            style={{ display: 'none' }}
          />
          {sheets === null ? null : sheets.length === 0 ? (
            <PlaceholderText>
              <Trans
                defaults={t('project:files.upload-sheet-placeholder')}
                components={{
                  plusIcon: (
                    <SparkelIcon
                      size="sm"
                      icon={faPlus}
                      color={['gray.500', 'gray.400']}
                    />
                  ),
                }}
              />
            </PlaceholderText>
          ) : (
            <SheetList sheets={sheets} projectId={projectId} readOnly={false} />
          )}
        </DragAndDrop>
      ) : sheets == null ? null : (
        // Render list of sheets in readonly mode
        <Box padding={2}>
          <SheetList sheets={sheets} projectId={projectId} readOnly={true} />
        </Box>
      )}
      <ConfirmModelUpdateModal
        isOpen={confirmUpdateIsOpen && !!filesToConfirm}
        onConfirm={async () => {
          invariant(filesToConfirm, 'No file to confirm');
          closeConfirmUpdate();
          const existingByName = new Map(
            (sheets ?? []).map((s) => [s.name, s])
          );
          await Promise.all(
            [...filesToConfirm].map(async (file) => {
              const current = existingByName.get(file.name);
              const nextVersion = current ? (current.version ?? 1) + 1 : 1;
              await uploadSheet(file, nextVersion, !!current);
            })
          );
          setFilesToConfirm(null);
        }}
        onClose={() => {
          setFilesToConfirm(null);
          closeConfirmUpdate();
        }}
      />
    </>
  );
}

type ModelListProps = {
  modelVersions: Dictionary<GetModelsForProjectQuery['models']>;
  isUploading?: boolean;
  handleUploadExistingVersion: (
    e: React.ChangeEvent<HTMLInputElement>,
    versionName: string
  ) => Promise<void>;
  handleDeleteModel: (versionName: string) => Promise<void>;
  readOnly: boolean;
};

type SheetListProps = {
  sheets: Sheet[];
  projectId: string;
  // isUploading?: boolean;
  // handleUploadExistingVersion: (
  //   e: React.ChangeEvent<HTMLInputElement>,
  //   versionName: string
  // ) => Promise<void>;
  // handleDeleteSheet: (versionName: string) => Promise<void>;
  readOnly: boolean;
};

const SheetList = ({ sheets, readOnly }: SheetListProps) => (
  <List>
    {sheets.map((sheet) => (
      <ListItem key={sheet.id}>
        <SheetItem sheet={sheet} readOnly={readOnly} />
      </ListItem>
    ))}
  </List>
);

const ModelList = ({
  modelVersions,
  isUploading,
  handleUploadExistingVersion,
  handleDeleteModel,
  readOnly,
}: ModelListProps) => (
  <List>
    {Object.entries(modelVersions)
      .sort((a, b) => a[0].localeCompare(b[0])) // Sorts according to versionName
      .map(([versionName, models]) => (
        <ListItem key={versionName}>
          <BimModelItem
            model={{
              models: sortBy(models, (model) => model.filename),
            }}
            isUploadDisabled={isUploading}
            handleUploadExistingVersion={(e) =>
              handleUploadExistingVersion(e, versionName)
            }
            deleteModelVersion={() => handleDeleteModel(versionName)}
            readOnly={readOnly}
          />
        </ListItem>
      ))}
  </List>
);
